import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { StandardEvents, CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/** The section this component fetches. It lives in no template on purpose. */
const SECTION_ID = 'mm-cart-recommendations';

/**
 * Fills the cart drawer's recommendation row.
 *
 * Horizon has no hook for content inside the cart drawer, so this mirrors what
 * `assets/product-recommendations.js` does on a product page, with two differences that
 * the drawer forces:
 *
 * - It cannot wait for an IntersectionObserver. The drawer is a closed `<dialog>`, so it
 *   never intersects. It loads when the drawer opens instead.
 * - The row lives inside `data-hydration-key="cart-drawer-inner"`, so every cart change
 *   morphs it back to the empty markup the server renders. Responses are cached per URL,
 *   so refilling after a change costs no request.
 *
 * Failure is silent by design: the container stays hidden rather than leaving a gap.
 *
 * @extends {Component}
 */
class MmCartRecommendations extends Component {
  /** @type {Map<string, string>} */
  #cache = new Map();

  /** @type {AbortController | null} */
  #activeFetch = null;

  /** The URLs behind the markup currently in the DOM, so a repeat open does nothing. */
  #loadedKey = /** @type {string | null} */ (null);

  /** Bumped by every load, so a slower one that started earlier never morphs last. */
  #loadToken = 0;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#onDrawerOpen);

    // The restore path in snippets/theme-drawer.liquid sets [open] before modules load,
    // so the open event may already have fired. Same guard cart-drawer.js uses.
    if (this.#themeDrawer?.hasAttribute('open')) this.#load();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#onDrawerOpen);
    this.#activeFetch?.abort();
  }

  #onDrawerOpen = () => {
    this.#load();
  };

  /** @param {import('@shopify/events').CartLinesUpdateEvent} event */
  #onCartLinesUpdate = (event) => {
    // The morph that follows a cart change empties this subtree, and the first product
    // may also have changed, so the memo of what is on screen no longer holds.
    //
    // The event is dispatched BEFORE the request, so loading right away read the old
    // product id and the morph then emptied the row with nothing to refill it: measured
    // when the gift of M08 became the first line and left. Waiting for the promise, and
    // then a frame, lets the morph land first.
    this.#loadedKey = null;
    const reload = () => requestAnimationFrame(() => requestAnimationFrame(() => this.#load()));
    if (event?.promise) event.promise.then(reload, reload);
    else reload();
  };

  /**
   * Builds one recommendations URL per candidate product, in the order the Liquid lists
   * them (the first, second and third product of the cart, gifts left out).
   * @returns {string[]} empty when the cart has no line to recommend against.
   */
  #buildUrls() {
    const { productIds } = this.dataset;

    return (productIds || '')
      .split(',')
      .filter(Boolean)
      .map((productId) => this.#buildUrl(productId))
      .filter((url) => url !== null);
  }

  /**
   * @param {string} productId
   * @returns {string | null}
   */
  #buildUrl(productId) {
    const { url, intent, limit, cartKey } = this.dataset;

    if (!url) return null;

    const target = new URL(url, window.location.origin);
    target.searchParams.set('section_id', SECTION_ID);
    target.searchParams.set('product_id', productId);
    target.searchParams.set('limit', limit || '6');
    if (intent) target.searchParams.set('intent', intent);

    // The section skips what is already in the cart, so the same first product with a
    // different cart is a different answer. The key is the cart's product ids: Shopify
    // ignores the parameter, and the cache stops serving a row that offers a line the
    // shopper has just added.
    if (cartKey) target.searchParams.set('mm_cart', cartKey);

    return target.toString();
  }

  /**
   * The same section rendered without a product: no `recommendations`, so it is the
   * collection alone. The last resort when no product of the cart answered at all --
   * measured on 2026-10-30, Shopify returns 404 for the recommendations of a product
   * that is not published on the online store (one added from the admin's preview),
   * and with that the row used to stay hidden instead of falling back.
   * @returns {string}
   */
  #buildFallbackUrl() {
    const root = window.Shopify?.routes?.root || '/';
    const target = new URL(root, window.location.origin);
    target.searchParams.set('section_id', SECTION_ID);
    if (this.dataset.cartKey) target.searchParams.set('mm_cart', this.dataset.cartKey);
    return target.toString();
  }

  /**
   * Asks for the first product's complementary picks; when it has none, for the second
   * product's, then the third's (asked on 2026-10-30). The first answer with any is
   * shown, topped up from the collection as the section does. When none has any, every
   * answer is the same collection-only row, so the first one is shown.
   */
  async #load() {
    const urls = this.#buildUrls();
    const key = urls.join(' ');

    if (!urls.length || key === this.#loadedKey) return;

    const token = ++this.#loadToken;

    try {
      /** @type {string | null} */
      let html = null;

      for (const url of urls) {
        const answer = await this.#fetch(url);
        if (token !== this.#loadToken) return;
        if (!answer?.trim()) continue;

        html ??= answer;
        if (this.#complementaryCount(answer) > 0) {
          html = answer;
          break;
        }
      }

      if (!html) {
        html = await this.#fetch(this.#buildFallbackUrl());
        if (token !== this.#loadToken) return;
      }

      if (!html?.trim()) return this.#stayHidden();

      await morphSection(SECTION_ID, html, { mode: 'hydration', injectStylesheet: true });
      if (token !== this.#loadToken) return;

      // An empty `recommendations.products` still returns a valid section, just without
      // the list. `intent=complementary` does that whenever Search & Discovery has no
      // complementary products loaded for the item, which is why this is checked in the
      // DOM rather than assumed from a 200.
      if (!this.querySelector('.mm-cart-recos__list')) return this.#stayHidden();

      this.#loadedKey = key;
      this.hidden = false;
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      this.#stayHidden(error);
    }
  }

  /**
   * @param {string} url
   * @returns {Promise<string | null>}
   */
  async #fetch(url) {
    const cached = this.#cache.get(url);
    if (cached != null) return cached;

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) return null;

      const text = await response.text();
      this.#cache.set(url, text);
      return text;
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * How many complementary picks an answer holds, before the collection tops it up.
   * The section writes it on its keyed div; a missing count reads as none.
   * @param {string} html
   * @returns {number}
   */
  #complementaryCount(html) {
    const root = new DOMParser().parseFromString(html, 'text/html');
    return Number(root.querySelector('[data-mm-complementary]')?.getAttribute('data-mm-complementary')) || 0;
  }

  /**
   * @param {unknown} [error]
   */
  #stayHidden(error) {
    this.hidden = true;
    if (error) console.error('Cart recommendations:', error);
  }
}

if (!customElements.get('mm-cart-recommendations')) {
  customElements.define('mm-cart-recommendations', MmCartRecommendations);
}

/**
 * [ AGREGAR ] on a tile of «Completa el look» (sections/mm-cart-recommendations.liquid).
 *
 * One available variant: added on click. Several (sizes): the click swaps the button
 * for the select and the change adds the size picked. The write is announced with the
 * same `CartLinesUpdateEvent` the product form uses, with the drawer's sections, so the
 * drawer, the line list, the footer and the header count all repaint themselves, and the
 * row above refills without the product just added. Same shape as the gift picker's
 * writer (assets/mm-cart-gift.js), which is private to that class.
 *
 * @typedef {object} RecoAddRefs
 * @property {HTMLButtonElement} [cta]
 * @property {HTMLSelectElement} [select]
 *
 * @extends {Component<RecoAddRefs>}
 */
class MmCartRecoAdd extends Component {
  #busy = false;

  open() {
    const { cta, select } = this.refs;
    if (!cta || !select) return;
    cta.hidden = true;
    cta.setAttribute('aria-expanded', 'true');
    select.hidden = false;
    select.focus();
  }

  /**
   * `event.target` and not `currentTarget`: Horizon delegates `on:click` from the
   * document (assets/component.js) and proxies `target` to the element that carries
   * the attribute, so `currentTarget` is the document.
   * @param {MouseEvent} event
   */
  add(event) {
    const button = event.target instanceof HTMLElement ? event.target : null;
    const variantId = Number(button?.dataset.variantId);
    if (variantId) this.#add(variantId);
  }

  addSelected() {
    const variantId = Number(this.refs.select?.value);
    if (variantId) this.#add(variantId);
  }

  /** @param {number} variantId */
  async #add(variantId) {
    if (this.#busy) return;
    this.#setBusy(true);

    const sections = [...document.querySelectorAll('cart-items-component[data-section-id]')]
      .map((element) => /** @type {HTMLElement} */ (element).dataset.sectionId)
      .join(',');

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: String(variantId), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }], sections }),
      });
      const body = await response.json();
      if (!response.ok || body.status) throw new Error(body.description || body.message || 'Add failed');

      const cart = await (await fetch(`${Theme.routes.cart_url}.js`)).json();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          sections: body.sections,
          items: cart.items,
          itemCount: cart.item_count,
          source: 'mm-cart-reco-add',
          didError: false,
        },
      });
    } catch (error) {
      console.error('Cart recommendations:', error);
      try {
        const cart = await (await fetch(`${Theme.routes.cart_url}.js`)).json();
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { items: cart.items, itemCount: cart.item_count, source: 'mm-cart-reco-add', didError: true },
        });
      } catch {
        deferred.reject(error);
      }
      this.dispatchEvent(
        new CartErrorEvent({ error: error instanceof Error ? error.message : 'Add failed', code: 'INVALID' })
      );
      this.#setBusy(false);
    }
  }

  /** @param {boolean} busy */
  #setBusy(busy) {
    this.#busy = busy;
    const { cta, select } = this.refs;
    if (cta) cta.disabled = busy;
    if (select) select.disabled = busy;
  }
}

if (!customElements.get('mm-cart-reco-add')) {
  customElements.define('mm-cart-reco-add', MmCartRecoAdd);
}
